/**
 * Session management utilities for session-based authentication
 * Handles CSRF tokens and session state checking.
 *
 * Phone and admin flows use separate cookies (phone.sid / admin.sid) and
 * separate CSRF tokens.  The "admin*" variants below mirror the phone
 * helpers but hit the /api/admin/* endpoints instead.
 */

// ── Phone CSRF token (in-memory) ──
let csrfToken = null;
const TRANSFER_TOKEN_REGEX = /^[0-9a-fA-F]{64}$/;

// ── Admin CSRF token (in-memory, separate from phone) ──
let adminCsrfToken = null;

/**
 * Get the current CSRF token, fetching from server if needed
 * @returns {Promise<string|null>} The CSRF token or null if not authenticated
 */
export async function getCsrfToken() {
  if (csrfToken) return csrfToken;
  
  try {
    const response = await fetch("/api/csrf-token", {
      credentials: "include",
    });
    
    if (!response.ok) {
      console.warn("Failed to fetch CSRF token - user may not be authenticated");
      return null;
    }
    
    const data = await response.json();
    csrfToken = data.csrfToken;
    return csrfToken;
  } catch (err) {
    console.error("Error fetching CSRF token:", err);
    return null;
  }
}

/**
 * Clear the cached CSRF token (call on logout)
 */
export function clearCsrfToken() {
  csrfToken = null;
}

/**
 * Check current session status
 * @returns {Promise<{authenticated: boolean, user?: object, error?: string}>}
 */
export async function checkSession() {
  try {
    const response = await fetch("/api/me", {
      credentials: "include",
    });
    
    if (response.status === 401) {
      return { authenticated: false, error: "SESSION_EXPIRED" };
    }
    
    if (!response.ok) {
      return { authenticated: false, error: "UNKNOWN_ERROR" };
    }
    
    const user = await response.json();
    
    // Fetch CSRF token after confirming authentication
    await getCsrfToken();
    
    return { authenticated: true, user };
  } catch (err) {
    console.error("Error checking session:", err);
    return { authenticated: false, error: err.message };
  }
}

/**
 * Refresh the session tokens
 * @returns {Promise<{success: boolean, tokenStatus?: object, error?: string}>}
 */
export async function refreshSession() {
  try {
    const token = await getCsrfToken();
    if (!token) {
      return { success: false, error: "NO_CSRF_TOKEN" };
    }
    
    const response = await fetch("/api/refresh-session", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
      },
    });
    
    if (response.status === 401) {
      clearCsrfToken();
      return { success: false, error: "SESSION_EXPIRED" };
    }
    
    if (!response.ok) {
      return { success: false, error: "REFRESH_FAILED" };
    }
    
    const data = await response.json();
    return { success: true, tokenStatus: data.tokenStatus };
  } catch (err) {
    console.error("Error refreshing session:", err);
    return { success: false, error: err.message };
  }
}

/**
 * Get ACS token from session (for ACS SDK token refresher)
 * @returns {Promise<{token: string, expiresOn: string}|null>}
 */
export async function getAcsToken() {
  try {
    const response = await fetch("/api/acs-token", {
      credentials: "include",
    });
    
    if (!response.ok) {
      console.error("Failed to get ACS token from session");
      return null;
    }
    
    return await response.json();
  } catch (err) {
    console.error("Error getting ACS token:", err);
    return null;
  }
}

/**
 * Complete a popup-to-iframe session transfer when cookies are partitioned.
 * @param {string} transferToken - One-time transfer token from the auth popup
 * @returns {Promise<{authenticated: boolean, user?: object, error?: string}>}
 */
export async function completeSessionTransfer(transferToken) {
  if (
    !transferToken ||
    typeof transferToken !== "string" ||
    !TRANSFER_TOKEN_REGEX.test(transferToken)
  ) {
    return { authenticated: false, error: "INVALID_TRANSFER_TOKEN" };
  }

  try {
    const response = await fetch("/api/session-transfer", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ transferToken }),
    });

    if (!response.ok) {
      clearCsrfToken();
      let errorCode = "SESSION_TRANSFER_FAILED";

      try {
        const errorBody = await response.json();
        if (errorBody?.error) {
          errorCode = errorBody.error;
        }
      } catch {
        // Ignore non-JSON error responses.
      }

      return { authenticated: false, error: errorCode };
    }

    clearCsrfToken();
    return await checkSession();
  } catch (err) {
    console.error("Error completing session transfer:", err);
    clearCsrfToken();
    return { authenticated: false, error: "SESSION_TRANSFER_FAILED" };
  }
}

/**
 * Logout - destroy server session
 * @returns {Promise<boolean>} Success status
 */
export async function logout() {
  try {
    const response = await authenticatedFetch("/api/logout", {
      method: "POST",
      credentials: "include",
    });
    
    clearCsrfToken();
    
    return response.ok;
  } catch (err) {
    console.error("Error during logout:", err);
    clearCsrfToken();
    return false;
  }
}

/**
 * Helper to make authenticated fetch requests with CSRF token
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options (method, body, etc.)
 * @returns {Promise<Response>}
 */
export async function authenticatedFetch(url, options = {}) {
  const fetchOptions = {
    ...options,
    credentials: "include",
    headers: {
      ...options.headers,
    },
  };
  
  // Add CSRF token for state-changing requests
  const method = (options.method || "GET").toUpperCase();
  if (["POST", "PUT", "DELETE", "PATCH"].includes(method)) {
    const token = await getCsrfToken();
    if (token) {
      fetchOptions.headers["X-CSRF-Token"] = token;
    }
  }
  
  // Add Content-Type for JSON bodies
  if (options.body && typeof options.body === "string") {
    fetchOptions.headers["Content-Type"] = fetchOptions.headers["Content-Type"] || "application/json";
  }
  
  return fetch(url, fetchOptions);
}

// ═══════════════════════════════════════════════════════════════════
// Admin-flow helpers — mirror the phone helpers but use /api/admin/*
// endpoints and a separate in-memory CSRF token.
// ═══════════════════════════════════════════════════════════════════

/**
 * Get the admin CSRF token, fetching from server if needed
 */
export async function getAdminCsrfToken() {
  if (adminCsrfToken) return adminCsrfToken;
  try {
    const response = await fetch("/api/admin/csrf-token", {
      credentials: "include",
    });
    if (!response.ok) return null;
    const data = await response.json();
    adminCsrfToken = data.csrfToken;
    return adminCsrfToken;
  } catch (err) {
    console.error("Error fetching admin CSRF token:", err);
    return null;
  }
}

/**
 * Clear the cached admin CSRF token
 */
export function clearAdminCsrfToken() {
  adminCsrfToken = null;
}

/**
 * Check current admin session status
 */
export async function checkAdminSession() {
  try {
    const response = await fetch("/api/admin/me", {
      credentials: "include",
    });
    if (response.status === 401) {
      return { authenticated: false, error: "SESSION_EXPIRED" };
    }
    if (!response.ok) {
      return { authenticated: false, error: "UNKNOWN_ERROR" };
    }
    const user = await response.json();
    await getAdminCsrfToken();
    return { authenticated: true, user };
  } catch (err) {
    console.error("Error checking admin session:", err);
    return { authenticated: false, error: err.message };
  }
}

/**
 * Complete a popup-to-parent admin session transfer.
 */
export async function completeAdminSessionTransfer(transferToken) {
  if (
    !transferToken ||
    typeof transferToken !== "string" ||
    !TRANSFER_TOKEN_REGEX.test(transferToken)
  ) {
    return { authenticated: false, error: "INVALID_TRANSFER_TOKEN" };
  }

  try {
    const response = await fetch("/api/admin/session-transfer", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ transferToken }),
    });

    if (!response.ok) {
      clearAdminCsrfToken();
      let errorCode = "SESSION_TRANSFER_FAILED";
      try {
        const errorBody = await response.json();
        if (errorBody?.error) errorCode = errorBody.error;
      } catch {
        // Ignore non-JSON responses.
      }
      return { authenticated: false, error: errorCode };
    }

    clearAdminCsrfToken();
    return await checkAdminSession();
  } catch (err) {
    console.error("Error completing admin session transfer:", err);
    clearAdminCsrfToken();
    return { authenticated: false, error: "SESSION_TRANSFER_FAILED" };
  }
}

/**
 * Authenticated fetch for admin endpoints — uses admin CSRF token
 */
export async function adminAuthenticatedFetch(url, options = {}) {
  const fetchOptions = {
    ...options,
    credentials: "include",
    headers: { ...options.headers },
  };

  const method = (options.method || "GET").toUpperCase();
  if (["POST", "PUT", "DELETE", "PATCH"].includes(method)) {
    const token = await getAdminCsrfToken();
    if (token) {
      fetchOptions.headers["X-CSRF-Token"] = token;
    }
  }

  if (options.body && typeof options.body === "string") {
    fetchOptions.headers["Content-Type"] =
      fetchOptions.headers["Content-Type"] || "application/json";
  }

  return fetch(url, fetchOptions);
}

/**
 * Admin logout — destroy admin session
 */
export async function adminLogout() {
  try {
    const response = await adminAuthenticatedFetch("/api/admin/logout", {
      method: "POST",
    });
    clearAdminCsrfToken();
    return response.ok;
  } catch (err) {
    console.error("Error during admin logout:", err);
    clearAdminCsrfToken();
    return false;
  }
}